import { watch } from 'vue';
import { jwtDecode } from 'jwt-decode';
import * as Sentry from '@sentry/vue';
import { clearStickyFiltersStorage } from '@/composables/useFilterUrlSync.js';

/**
 * @description This function is used to decode the token.
 * @param {string} token - The token to decode
 * @returns {Object} - The decoded token
 */
export const decodeToken = (token) => {
  if (!token || typeof token !== 'string') return null;
  try {
    return jwtDecode(token);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * @description This function waits for Auth0 to be ready.
 * @param {import('@auth0/auth0-vue').Auth0VueClient} auth0 - Auth0 instance
 * @param {number} timeout - The timeout in milliseconds
 * @returns {Promise<void>} - Promise that resolves when Auth0 is ready
 */
const waitForAuth0ToBeReady = async (auth0, timeout = 3000) => {
  const start = Date.now();

  while (auth0.isLoading.value && Date.now() - start < timeout) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  if (auth0.isLoading.value) {
    throw new Error('Auth0 did not finish loading in time');
  }
};

/**
 * @description This function is used to guard the router.
 * It checks if the user has the required permission to access the route.
 * If the user does not have the required permission, it redirects to the not-found page.
 * If the user has the required permission, it redirects to the route. If the user is not authenticated, it redirects to the login page.
 * If the user is authenticated, it redirects to the route.
 * @param {import('@auth0/auth0-vue').Auth0VueClient} auth0 - Auth0 instance
 * @param {ReturnType<import('@/stores/auth').useAuthStore>} authStore - Auth store return value from useAuthStore()
 * @returns {import('vue-router').NavigationGuard}
 */
export const checkAuthAndPermission = (auth0, authStore) => {
  return async (to, _from, next) => {
    const requiredPermission = to.meta.permission;
    const isRoot = to.name === 'root';

    if (requiredPermission || isRoot) {
      try {
        if (auth0.isLoading.value) {
          await waitForAuth0ToBeReady(auth0);
        }

        const token = await auth0.getAccessTokenSilently();
        authStore.setToken(token);

        if (isRoot) {
          // Lazy import to avoid circular deps
          const { default: router } = await import('@/router');
          return next(firstAccessibleByPermissionFromRouter(auth0, authStore, router));
        }

        const hasPermission = Array.isArray(requiredPermission)
          ? authStore.hasAnyPermission(requiredPermission)
          : authStore.hasPermission(requiredPermission);
        if (!hasPermission) {
          return next({ name: 'not-found' });
        } else {
          return next();
        }
      } catch (error) {
        console.error('Error in routerGuard:', error);
        return next({ name: 'login' });
      }
    } else {
      return next();
    }
  };
};

/**
 * @description This function redirects authenticated users away from auth pages (login, forgot-password, etc.)
 * If the user is authenticated, it redirects them to the appropriate page based on their role.
 * If the user is not authenticated, it allows access to the auth page.
 * @param {import('@auth0/auth0-vue').Auth0VueClient} auth0 - Auth0 instance
 * @param {ReturnType<import('@/stores/auth').useAuthStore>} authStore - Auth store return value from useAuthStore()
 * @returns {import('vue-router').NavigationGuard}
 */
export const redirectIntoAppIfAuthenticated = (auth0, authStore, router) => {
  return async (_to, _from, next) => {
    try {
      if (auth0.isLoading.value) {
        await waitForAuth0ToBeReady(auth0);
      }

      if (auth0.isAuthenticated.value) {
        return next(firstAccessibleByPermissionFromRouter(auth0, authStore, router));
      } else {
        return next();
      }
    } catch (error) {
      console.error('Error in redirectIntoAppIfAuthenticated:', error);
      return next();
    }
  };
};

/**
 * @description Creates a beforeEnter guard for auth pages that redirects authenticated users
 * This helper function handles the dynamic imports to avoid circular dependencies
 * @returns {import('vue-router').NavigationGuard}
 */
export const createRedirectIfAuthenticatedGuard = () => {
  return (to, from, next) => {
    import('@/plugins/auth0').then(({ auth0Service }) => {
      import('@/stores/auth').then(({ useAuthStore }) => {
        import('@/router').then(({ default: router }) => {
          const auth0 = auth0Service.getAuth0Instance();
          const authStore = useAuthStore();
          redirectIntoAppIfAuthenticated(auth0, authStore, router)(to, from, next);
        });
      });
    });
  };
};

/**
 * @description Compute the first accessible, non-param route for the current user
 * by inspecting the registered routes on the active router. This is permission-driven
 * and avoids hard-coding route names. We optionally honor `meta.menuOrder` so the
 * landing route can stay in the same order as the sidebar/navigation.
 *
 * Selection rules:
 *  - Only consider named routes that declare `meta.permission`
 *  - Exclude parameterized paths (i.e., any route with ':' in its path)
 *  - Sort by `meta.menuOrder` ascending when present; unknowns go last
 *  - Pick the first route where `authStore.hasPermission(meta.permission)` is true
 *
 * @param {import('@auth0/auth0-vue').Auth0VueClient} auth0 - Auth0 instance
 * @param {ReturnType<import('@/stores/auth').useAuthStore>} authStore - Auth store instance (must expose hasPermission)
 * @param {import('vue-router').Router} router - Vue Router instance
 * @returns {{ name: string }} - A location object (by route name) for use with `next()`
 */
const firstAccessibleByPermissionFromRouter = (auth0, authStore, router) => {
  // Pull all named, non-param routes that declare a permission.
  const isEligible = (route) => route.name && route.meta && route.meta.permission && !route.path.includes(':'); // avoid param routes

  const candidates = router
    .getRoutes()
    .filter(isEligible)
    .sort((a, b) => (a.meta.menuOrder ?? 9999) - (b.meta.menuOrder ?? 9999));

  for (const route of candidates) {
    if (authStore.hasPermission(route.meta.permission)) {
      return { name: route.name };
    }
  }
  // Nothing matched -> Something is very off -> send to a safe holding page
  // It is likely the user is not authenticated, but we'll end the session to start fresh
  clearStickyFiltersStorage();
  auth0.logout({ logoutParams: { returnTo: window.location.origin + '/auth/login' } });
};

/**
 * Sets up reactive Sentry user context synchronization with Auth0 user state.
 * @param {import('@auth0/auth0-vue').Auth0VueClient} auth0 - Auth0 plugin instance
 * @returns {import('vue').WatchStopHandle} Watcher handle (call .stop() to cleanup if needed)
 */
export const setSentryUserFromAuthStore = (auth0) => {
  watch(
    () => auth0.user.value,
    (user) => {
      Sentry.setUser(user || null);
    },
    { immediate: true, deep: true }
  );
};
